import { useId } from "react";
import { useI18N } from "common/hooks";
import type { VenueDetails } from "common/types";
import { cn } from "@/lib/utils";
import { formatOpeningTime, venueLocalDay } from "@/lib/openingHours";
import { useIsClient } from "@/hooks/useIsClient";

/** Google's day numbers (0 = Sunday), listed Monday first. */
const WEEK = [
  { day: 1, labelKey: "monday" },
  { day: 2, labelKey: "tuesday" },
  { day: 3, labelKey: "wednesday" },
  { day: 4, labelKey: "thursday" },
  { day: 5, labelKey: "friday" },
  { day: 6, labelKey: "saturday" },
  { day: 0, labelKey: "sunday" },
];

type VenueAboutBlockProps = {
  venueDetails: VenueDetails;
  headingLevel?: 2 | 3;
  /** Off where the page already prints the address (the complex Overview). */
  showAddress?: boolean;
  className?: string;
};

/**
 * The venue's address and weekly opening hours as plain text, so they are readable near the
 * top of the server-rendered page.
 */
export default function VenueAboutBlock({
  venueDetails,
  headingLevel = 2,
  showAddress = true,
  className,
}: VenueAboutBlockProps) {
  const i18n = useI18N();
  const id = useId();
  const isClient = useIsClient();
  const Heading = headingLevel === 3 ? "h3" : "h2";

  const address = showAddress ? venueDetails.address?.trim() : undefined;
  const openingHours = venueDetails.openingHours ?? [];
  // Same rule as the header: a temporarily closed venue has no hours to list.
  const showHours =
    venueDetails.businessStatus !== "CLOSED_TEMPORARILY" &&
    openingHours[0]?.mode === 0;
  // Browser only: the cached page can be a day old, so a server-rendered "today" could be wrong.
  const today = isClient ? venueLocalDay(openingHours[0]?.utcOffset) : undefined;

  if (!address && !showHours) return null;

  const timeLabels = {
    am: i18n.get("am"),
    pm: i18n.get("pm"),
    invalid: i18n.get("invalidTime"),
  };
  const periodsFor = (day: number) =>
    openingHours
      .filter((hour) => hour.day === day)
      .map(
        (hour) =>
          `${formatOpeningTime(hour.openTime, timeLabels)} - ${formatOpeningTime(hour.closeTime, timeLabels)}`,
      )
      .join(", ");

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className={cn("flex flex-col gap-3", className)}
    >
      <Heading
        id={`${id}-heading`}
        className="title-s-semibold text-fg-neutral-strong"
      >
        {i18n.get("aboutThisPlace")}
      </Heading>

      {address && (
        <div className="flex flex-col gap-0.5">
          <p className="label-s-medium text-fg-neutral-subtle">
            {i18n.get("address")}
          </p>
          <p className="body-s-regular text-fg-neutral-strong">{address}</p>
        </div>
      )}

      {showHours && (
        <div className="flex flex-col gap-0.5">
          <p id={`${id}-hours`} className="label-s-medium text-fg-neutral-subtle">
            {i18n.get("openingHours")}
          </p>
          <ul aria-labelledby={`${id}-hours`} className="flex flex-col gap-0.5">
            {WEEK.map(({ day, labelKey }) => {
              const isToday = day === today;
              const Text = isToday ? "strong" : "span";
              return (
                <li
                  key={day}
                  aria-current={isToday ? "date" : undefined}
                  className="flex justify-between gap-3 body-s-regular text-fg-neutral-strong"
                >
                  <Text>{i18n.get(labelKey)}</Text>
                  <Text className="text-right">
                    {periodsFor(day) || i18n.get("closed")}
                  </Text>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
